/**
 * Centralna konfiguracja SEO — używać w head() każdej podstrony.
 *
 * Po podpięciu domeny w produkcji:
 * 1. Zaktualizuj SITE_URL na produkcyjny adres (np. https://synctech.pl).
 * 2. Wygeneruj OG image (1200x630) i wgraj jako /og-default.jpg do public/.
 * 3. Wstaw realny GA4_MEASUREMENT_ID (G-XXXXXXXXXX).
 */
export const SITE_URL = "https://synctech.pl";
export const SITE_NAME = "SyncTech";
export const SITE_LOCALE = "pl_PL";
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-default.jpg`;
export const GA4_MEASUREMENT_ID = "G-BHG6T8NJPF"; // SyncTech – www.synctech.pl
export const GTM_CONTAINER_ID = "GTM-NGCDS2LH"; // SyncTech – Google Tag Manager
export const CLARITY_PROJECT_ID = ""; // TODO: wstaw ID z https://clarity.microsoft.com (np. "abc123def4")

/** Zbuduj absolutny URL na bazie path (np. "/platformy/baselinker"). */
export function absUrl(path: string): string {
  if (!path.startsWith("/")) path = `/${path}`;
  return `${SITE_URL}${path}`;
}

type MetaTag = { title?: string } | { name: string; content: string } | { property: string; content: string } | { charSet: string };
type LinkTag = { rel: string; href: string; hrefLang?: string };

export interface SeoMetaInput {
  title: string;
  description: string;
  path: string; // np. "/platformy/baselinker"
  image?: string; // absolutny URL OG image
  type?: "website" | "article";
  noindex?: boolean;
  ogTitle?: string; // override dla og:title / twitter:title
  ogDescription?: string; // override dla og:description / twitter:description
}

/**
 * Generuje pełen zestaw meta tagów (title, description, canonical, OG, Twitter)
 * dla pojedynczej podstrony. Używać w head() w createFileRoute.
 */
export function buildSeoMeta(input: SeoMetaInput): { meta: MetaTag[]; links: LinkTag[] } {
  const { title, description, path, image = DEFAULT_OG_IMAGE, type = "website", noindex, ogTitle, ogDescription } = input;
  const url = absUrl(path);
  const finalOgTitle = ogTitle ?? title;
  const finalOgDescription = ogDescription ?? description;


  const meta: MetaTag[] = [
    { title },
    { name: "description", content: description },
    { name: "robots", content: noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1" },

    // Open Graph
    { property: "og:type", content: type },
    { property: "og:title", content: finalOgTitle },
    { property: "og:description", content: finalOgDescription },
    { property: "og:url", content: url },
    { property: "og:site_name", content: SITE_NAME },
    { property: "og:locale", content: SITE_LOCALE },
    { property: "og:image", content: image },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },

    // Twitter
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: finalOgTitle },
    { name: "twitter:description", content: finalOgDescription },
    { name: "twitter:image", content: image },
  ];

  const links: LinkTag[] = [
    { rel: "canonical", href: url },
  ];

  return { meta, links };
}

/** Lista wszystkich publicznych route'ów do sitemap.xml. */
export const SITEMAP_ROUTES: Array<{ path: string; priority: number; changefreq: string }> = [
  { path: "/", priority: 1.0, changefreq: "weekly" },
  { path: "/cennik", priority: 0.95, changefreq: "monthly" },
  { path: "/kalkulator", priority: 0.95, changefreq: "monthly" },
  // platformy
  { path: "/platformy/baselinker", priority: 0.9, changefreq: "monthly" },
  { path: "/platformy/sellasist", priority: 0.9, changefreq: "monthly" },
  { path: "/platformy/shopgold", priority: 0.9, changefreq: "monthly" },
  { path: "/platformy/shoper", priority: 0.9, changefreq: "monthly" },
  { path: "/platformy/shopify", priority: 0.9, changefreq: "monthly" },
  { path: "/platformy/prestashop", priority: 0.9, changefreq: "monthly" },
  // integracje
  { path: "/integracje/sklepy-marketplace", priority: 0.8, changefreq: "monthly" },
  { path: "/integracje/erp-baselinker", priority: 0.8, changefreq: "monthly" },
  { path: "/integracje/logistyka-kurierzy", priority: 0.8, changefreq: "monthly" },
  { path: "/integracje/platnosci", priority: 0.8, changefreq: "monthly" },
  { path: "/integracje/ads", priority: 0.8, changefreq: "monthly" },
  { path: "/integracje/email-automation", priority: 0.8, changefreq: "monthly" },
  { path: "/integracje/obsluga", priority: 0.8, changefreq: "monthly" },
  { path: "/integracje/analityka", priority: 0.8, changefreq: "monthly" },
  { path: "/integracje/fakturownia", priority: 0.8, changefreq: "monthly" },
  { path: "/integracje/apilo", priority: 0.8, changefreq: "monthly" },
  // blog
  { path: "/blog", priority: 0.9, changefreq: "weekly" },
  { path: "/blog/jak-polaczyc-baselinker-z-shopify", priority: 0.8, changefreq: "monthly" },
  { path: "/blog/migracja-z-shopgold-do-baselinker", priority: 0.8, changefreq: "monthly" },
  { path: "/blog/integracja-sellasist-allegro-krok-po-kroku", priority: 0.8, changefreq: "monthly" },
  { path: "/blog/baselinker-koszty-wdrozenia-2026", priority: 0.8, changefreq: "monthly" },
  { path: "/blog/automatyzacja-zamowien-baselinker", priority: 0.8, changefreq: "monthly" },
  { path: "/blog/jak-wybrac-kuriera-dla-sklepu-internetowego", priority: 0.8, changefreq: "monthly" },
  { path: "/blog/google-ads-dla-ecommerce-przewodnik-2026", priority: 0.8, changefreq: "monthly" },
  { path: "/blog/facebook-conversions-api-wdrozenie-krok-po-kroku", priority: 0.8, changefreq: "monthly" },
  { path: "/blog/automatyzacja-faktur-fakturownia-baselinker", priority: 0.8, changefreq: "monthly" },
  { path: "/blog/inpost-paczkomaty-integracja-baselinker", priority: 0.8, changefreq: "monthly" },
  { path: "/blog/integracja-baselinker-allegro-przewodnik", priority: 0.8, changefreq: "monthly" },
  // porównania
  { path: "/porownanie", priority: 0.9, changefreq: "monthly" },
  { path: "/porownanie/baselinker-vs-sellasist", priority: 0.85, changefreq: "monthly" },
  { path: "/porownanie/shopgold-vs-shoper", priority: 0.85, changefreq: "monthly" },
  { path: "/porownanie/shopify-vs-prestashop", priority: 0.85, changefreq: "monthly" },
  // legal
  { path: "/polityka-prywatnosci", priority: 0.3, changefreq: "yearly" },
  { path: "/regulamin", priority: 0.3, changefreq: "yearly" },
];
